import React, { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  AccountBalanceWallet as WalletIcon,
  Refresh as RefreshIcon,
  CardMembership as MembershipIcon,
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon,
  Palette as PaletteIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import {
  getCreatorDashboard,
  getCreatorPayouts,
  updatePaymentDetails,
  requestPayout,
  requestMembershipExtension,
  CreatorDashboard as DashboardData,
  Payout,
} from './creatorApi';

const CreatorDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);

  // Payout dialog
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [requestingPayout, setRequestingPayout] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashboardData, payoutData] = await Promise.all([
        getCreatorDashboard(),
        getCreatorPayouts({ limit: 10 }),
      ]);
      setDashboard(dashboardData);
      setPayouts(payoutData);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('not_a_creator');
      } else {
        setError(err.response?.data?.error || 'Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleCopyCode = () => {
    if (dashboard?.creator.promo_code) {
      navigator.clipboard.writeText(dashboard.creator.promo_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSavePayment = async () => {
    if (!paypalEmail || !paypalEmail.includes('@')) {
      setError('Please enter a valid PayPal email');
      return;
    }

    setSavingPayment(true);
    setError(null);
    try {
      await updatePaymentDetails('paypal', { email: paypalEmail });
      setSuccessMessage('Payment details saved successfully!');
      setPaymentDialogOpen(false);
      fetchDashboard();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save payment details');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleRequestPayout = async () => {
    setRequestingPayout(true);
    setError(null);
    try {
      await requestPayout();
      setSuccessMessage('Payout requested! We\'ll process it within 5-7 business days.');
      setPayoutDialogOpen(false);
      fetchDashboard();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to request payout');
    } finally {
      setRequestingPayout(false);
    }
  };

  const [requestingExtension, setRequestingExtension] = useState(false);

  const handleRequestExtension = async () => {
    setRequestingExtension(true);
    setError(null);
    try {
      const result = await requestMembershipExtension();
      setSuccessMessage(result.message);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit extension request');
    } finally {
      setRequestingExtension(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'active': return 'success';
      case 'approved': return 'info';
      case 'trialing': return 'info';
      case 'pending': return 'warning';
      case 'processing': return 'warning';
      case 'completed': return 'success';
      case 'suspended': return 'error';
      case 'cancelled': return 'error';
      case 'churned': return 'error';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !dashboard) {
    if (error === 'not_a_creator') {
      return (
        <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', textAlign: 'center', mt: 8 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Creator Program
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            It looks like you're not part of our creator program yet. If you're interested, learn more about it and apply!
          </Typography>
          <Button
            variant="contained"
            href="https://snackdapp.com/creator-program"
            target="_blank"
          >
            Learn More
          </Button>
        </Box>
      );
    }
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!dashboard) {
    return null;
  }

  if (!dashboard.creator.terms_accepted_at) {
    return <Navigate to="/creator-agreement" replace />;
  }

  const { creator, referrals, revenue } = dashboard;
  const canRequestPayout = revenue.pending_payout > 0;

  const brandAssets = [
    { file: 'logo-square.png', label: 'Logo (Square)', desc: '1000 x 1000' },
    { file: 'logo-horizontal.png', label: 'Logo (Horizontal)', desc: '1000 x 400, cream bg' },
    { file: 'logo-horizontal-transparent.png', label: 'Logo (Transparent)', desc: '1000 x 400' },
    { file: 'logo-horizontal-dark.png', label: 'Logo (Dark BG)', desc: '1000 x 400' },
    { file: 'app-icon.png', label: 'App Icon', desc: 'App store icon' },
    { file: 'splash-icon.png', label: 'Small Icon', desc: 'Compact icon' },
    { file: 'branding-guide.jpg', label: 'Branding Guide', desc: 'Colors, fonts & moodboard' },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Creator Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back, {creator.name}!
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={creator.status}
            color={getStatusColor(creator.status)}
            size="small"
          />
          <IconButton onClick={fetchDashboard} size="small">
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {creator.status === 'suspended' && (
        <Alert severity="error" variant="filled" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Your creator account has been suspended
          </Typography>
          <Typography variant="body2">
            Your promo code has been deactivated. If you have an unpaid balance, it will be paid out within 30 days to the PayPal address on file. Your Snack'd membership remains active until the end of your current membership period. If you have questions, contact support@snackdapp.com.
          </Typography>
        </Alert>
      )}

      {/* Promo Code Card */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, #032736 0%, #0a4d6e 100%)',
          color: '#F78764',
        }}
      >
        <Typography variant="overline" sx={{ opacity: 0.8, color: 'inherit' }}>
          Your Promo Code
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          mt={1}
        >
          <Typography
            variant="h3"
            fontWeight="bold"
            sx={{
              letterSpacing: 2,
              fontFamily: 'monospace',
              color: 'inherit',
            }}
          >
            {creator.promo_code || 'Not assigned'}
          </Typography>
          {creator.promo_code && (
            <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
              <IconButton
                onClick={handleCopyCode}
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                }}
              >
                {copied ? <CheckIcon /> : <CopyIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Typography variant="body2" sx={{ mt: 2, opacity: 0.8, color: 'inherit' }}>
          Share this code with your audience. They get an extended 30-day free trial!
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, opacity: 0.6, color: 'inherit' }}>
          Used {creator.code_uses || 0} times
        </Typography>
      </Paper>

      {/* Membership Card */}
      {dashboard.membership && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <MembershipIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Creator Membership
            </Typography>
            <Chip
              label={dashboard.membership.status}
              size="small"
              color={getStatusColor(dashboard.membership.status)}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Start Date</Typography>
              <Typography fontWeight="medium">{formatDate(dashboard.membership.start_date)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">End Date</Typography>
              <Typography fontWeight="medium">{formatDate(dashboard.membership.end_date)}</Typography>
            </Box>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              onClick={handleRequestExtension}
              disabled={requestingExtension}
              size="small"
            >
              {requestingExtension ? <CircularProgress size={20} /> : 'Request Extension'}
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Stats Grid */}
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <PeopleIcon color="primary" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                  Total Signups
                </Typography>
              </Stack>
              <Typography variant="h4" fontWeight="bold">
                {referrals.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <TrendingUpIcon color="success" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                  Active Subscribers
                </Typography>
              </Stack>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {referrals.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <MoneyIcon color="primary" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                  Total Earned
                </Typography>
              </Stack>
              <Typography variant="h4" fontWeight="bold">
                {formatCurrency(revenue.total_earned)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <WalletIcon color="warning" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                  Pending Payout
                </Typography>
              </Stack>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {formatCurrency(revenue.pending_payout)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Referral Breakdown & Payout Section */}
      <Grid container spacing={3}>
        {/* Referral Breakdown */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Referral Breakdown
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">New this month</Typography>
                <Typography fontWeight="medium">{referrals.new_this_month}</Typography>
              </Stack>
              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Currently trialing</Typography>
                <Chip label={referrals.trialing} size="small" color="info" />
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Active subscribers</Typography>
                <Chip label={referrals.active} size="small" color="success" />
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Cancelled</Typography>
                <Chip label={referrals.cancelled} size="small" color="default" />
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Churned</Typography>
                <Chip label={referrals.churned} size="small" color="error" />
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        {/* Payout Section */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Payouts
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Total paid out</Typography>
                <Typography fontWeight="medium">{formatCurrency(revenue.total_paid)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Available for payout</Typography>
                <Typography fontWeight="bold" color="warning.main">
                  {formatCurrency(revenue.pending_payout)}
                </Typography>
              </Stack>
              <Divider />
              <Typography variant="body2" color="text.secondary">
                No minimum payout threshold. Payouts are processed on the 1st of each month via PayPal.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => setPaymentDialogOpen(true)}
                  fullWidth={isMobile}
                >
                  {dashboard ? 'Update PayPal' : 'Set Up PayPal'}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setPayoutDialogOpen(true)}
                  disabled={!canRequestPayout}
                  fullWidth={isMobile}
                >
                  Request Payout
                </Button>
              </Stack>
              {revenue.pending_payout === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No balance yet. Keep sharing your code to start earning!
                </Typography>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Creator Assets */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <PaletteIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Brand Assets
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Download logos and branding materials for your content. Right-click or tap and hold to save.
        </Typography>
        <Grid container spacing={2}>
          {brandAssets.map((asset) => (
            <Grid size={{ xs: 6, sm: 4, md: 3 }} key={asset.file}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <Box
                  sx={{
                    height: 120,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: asset.file.includes('dark') ? '#1a1a2e' : '#f5f5f5',
                    p: 1,
                  }}
                >
                  <Box
                    component="img"
                    src={`/creator-assets/${asset.file}`}
                    alt={asset.label}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
                <CardContent sx={{ py: 1.5, px: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" fontWeight="medium" noWrap>
                    {asset.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {asset.desc}
                  </Typography>
                  <Box mt={1}>
                    <Button
                      component="a"
                      href={`/creator-assets/${asset.file}`}
                      download={asset.file}
                      size="small"
                      startIcon={<DownloadIcon />}
                      fullWidth
                      variant="outlined"
                    >
                      Download
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Creator Program Guidelines */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <MenuBookIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Creator Program Guidelines
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={1}>
          <strong>Brand:</strong> Snack'd &mdash; <strong>Core Message:</strong> <em>One meal, everyone eats.</em>
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          A meal planning app for families with picky eaters, different diets, &amp; food allergies.
        </Typography>

        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">What Snack'd Is</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>Snack'd is a meal planning app built specifically for:</Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 2 }}>
              <li>Families with picky eaters</li>
              <li>Kids with food allergies</li>
              <li>Households with multiple dietary needs</li>
              <li>Parents who are tired of cooking multiple meals every night</li>
            </Typography>
            <Typography variant="body2" paragraph>Snack'd creates:</Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
              <li>One family meal plan with built-in customizations</li>
              <li>A full shopping list</li>
              <li>Less stress at dinnertime</li>
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">What We're Looking For</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              We love authentic, real-life content! Real parents, real kitchens, real picky eaters, real frustration + real relief.
            </Typography>
            <Typography variant="body2" paragraph>Your content should feel like:</Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 2 }}>
              <li>A facetime to a friend</li>
              <li>A late-night mom TikTok</li>
              <li>A "you won't believe this fixed our dinners" moment</li>
              <li>Most importantly: raw, authentic, and real</li>
            </Typography>
            <Typography variant="body2">
              We're happy with content on any platform! As long as advertising guidelines are followed and users are linked to Snack'd, you can post on social media, your blog, or even share directly with friends!
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">Content Pillars</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              Your video should focus on at least one of these pillars:
            </Typography>
            <Typography variant="subtitle2" gutterBottom>The Struggle</Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 2 }}>
              <li>Cooking multiple meals</li>
              <li>Kids refusing everything</li>
              <li>Allergies + picky eaters at the same table</li>
              <li>Dreading the "what's for dinner" question</li>
            </Typography>
            <Typography variant="subtitle2" gutterBottom>The Win</Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 2 }}>
              <li>One meal everyone can eat</li>
              <li>Less stress, faster planning</li>
              <li>Happier dinners</li>
            </Typography>
            <Typography variant="subtitle2" gutterBottom>The "I Wish This Existed Sooner" Moment</Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
              <li>Emotional relief</li>
              <li>Feeling seen as a parent</li>
              <li>Not being alone in the struggle</li>
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">Do's &amp; Don'ts</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="success.main" gutterBottom>DO</Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>Be honest</li>
                  <li>Show your real routine</li>
                  <li>Speak casually</li>
                  <li>Share your actual frustration</li>
                  <li>Show the app naturally in use</li>
                  <li>Say "picky eater," "allergies," or "multiple diets" if applicable</li>
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="error.main" gutterBottom>DON'T</Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>Sound scripted or robotic</li>
                  <li>Over-promise results</li>
                  <li>Bash competitors by name</li>
                  <li>Use profanity</li>
                  <li>Make medical or nutritional claims</li>
                </Typography>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">Script Concepts</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" paragraph>
              You don't need to follow these &mdash; they're just for creative direction!
            </Typography>
            {[
              { title: '"Three Dinners vs One"', hook: 'I used to cook three separate meals every single night...', middle: 'One for my picky kid, one for my partner, and then something different for me because of allergies.', payoff: "Now Snack'd builds one meal with options for everyone and I don't feel like a short-order cook anymore." },
              { title: '"Picky Eater Parent Confession"', hook: 'No one talks about how exhausting picky eating actually is.', middle: 'I used to sit down already stressed before dinner even started.', payoff: 'This app actually helped us stop fighting over food.' },
              { title: '"What\'s for Dinner?" Stress Test', hook: 'When your kid asks "what\'s for dinner" and you panic...', middle: 'I used to freeze because I knew nothing would work for everyone.', payoff: "Snack'd just tells me what works for ALL of us." },
              { title: '"Allergies + Picky = Chaos"', hook: 'We have allergies AND a picky eater... I was losing my mind.', middle: "Snack'd actually builds meals around that.", payoff: 'For the first time, dinner feels possible again.' },
              { title: '"Shopping List Win"', hook: "I never realized how much mental space meal planning took...", middle: 'Now I just open the app and my grocery list is ready.', payoff: "It's life-changing." },
            ].map((concept) => (
              <Box key={concept.title} sx={{ mb: 2, pl: 2, borderLeft: 2, borderColor: 'divider' }}>
                <Typography variant="subtitle2" gutterBottom>{concept.title}</Typography>
                <Typography variant="body2"><strong>Hook:</strong> "{concept.hook}"</Typography>
                <Typography variant="body2"><strong>Middle:</strong> "{concept.middle}"</Typography>
                <Typography variant="body2"><strong>Payoff:</strong> "{concept.payoff}"</Typography>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">Hashtags</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>Use 2-5 max:</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {['#PickyEater', '#MomLife', '#DinnerStruggles', '#FoodAllergies', '#FamilyMeals', '#SnackdApp', '#DinnerTime', '#MealPrep', '#MealPlanning'].map((tag) => (
                <Chip key={tag} label={tag} size="small" variant="outlined" />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Most importantly &mdash; we care that the content is authentic and feels aligned with you!
          </Typography>
        </Box>
      </Paper>

      {/* Payout History */}
      {payouts.length > 0 && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Payout History
          </Typography>
          <TableContainer>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  {!isMobile && <TableCell>Reference</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {payouts.map((payout) => (
                  <TableRow key={payout.payout_id}>
                    <TableCell>{formatDate(payout.created_at)}</TableCell>
                    <TableCell>{formatCurrency(payout.amount)}</TableCell>
                    <TableCell>
                      <Chip
                        label={payout.status}
                        size="small"
                        color={getStatusColor(payout.status)}
                      />
                    </TableCell>
                    {!isMobile && (
                      <TableCell>
                        {payout.payment_reference || '-'}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Payment Details Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>PayPal Payment Details</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the PayPal email address where you'd like to receive payouts.
          </Typography>
          <TextField
            label="PayPal Email"
            type="email"
            fullWidth
            value={paypalEmail}
            onChange={(e) => setPaypalEmail(e.target.value)}
            placeholder="your-email@example.com"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSavePayment}
            disabled={savingPayment || !paypalEmail}
          >
            {savingPayment ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request Payout Dialog */}
      <Dialog
        open={payoutDialogOpen}
        onClose={() => setPayoutDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request Payout</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You're requesting a payout of <strong>{formatCurrency(revenue.pending_payout)}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The payout will be sent to your PayPal account within 5-7 business days.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayoutDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRequestPayout}
            disabled={requestingPayout}
          >
            {requestingPayout ? <CircularProgress size={20} /> : 'Confirm Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreatorDashboard;
